import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link, Outlet } from "react-router-dom";
import useApi from "../../hooks/useApi";
import useApp from "../../hooks/useApp";
import { PolicyItemList } from "./PolicyItemList";
import useReload from "../../hooks/useReload";
import usePagination from "../../hooks/usePagination";
import Swal from "sweetalert2";
import PolicyItemView from "./PolicyItemView";
import { Button, Modal, Form, Row, Col, Accordion } from "react-bootstrap";
import useSortableTableHeader from "../../hooks/useSortableTableHeader";
import PolicyItemStatus, { activeStatus } from "../../widgets/PolicyItemStatus";
import moment from "moment";

const PolicyView = ({ scope }) => {
  const [policy, setPolicy] = useState({});
  const [policies, setPolicies] = useState([]);
  const [classifier, setClassifier] = useState();
  const [markedPolicies, setMarkedPolicies] = useState([]);
  const [markedAll, setMarkedAll] = useState(false);
  const { id } = useParams();
  const { reload } = useReload();
  const navigate = useNavigate();
  const { api, abortSignal, auth } = useApi();
  const { searchParamString } = usePagination();
  const { showAlert } = useApp();

  const [showSendNotice, setSendNotice] = useState(false);
  const handleCloseSendNotice = () => setSendNotice(false);
  const handleShowSendNotice = () => setSendNotice(true);

  const { TableHeader } = useSortableTableHeader();

  const classifierRef = useRef();

  const deletePolicy = async () => {
    try {
      const response = await api.delete(`/policies/${id}`);

      if (response.ok) navigate(`/policies`, { replace: true });
    } catch (error) {
      console.error(error);
    }
  };

  const closePolicy = async () => {
    const callClose = async () => {
      try {
        const response = await api.update(`/policies/${id}/close`);

        if (response.ok) {
          Swal.fire("Policy closed!", `Policy has been closed`, "success");
          navigate(`/policies`);
        } else {
          Swal.fire("Close failed!", `Policy not close`, "info");
        }
      } catch (error) {
        Swal.fire(
          "Close failed!",
          `Policy not close - ${error.message}`,
          "info"
        );
      }
    };
    Swal.fire({
      title: "Close Policy?",
      text: "Do you want to continue",
      icon: "warning",
      allowOutsideClick: false,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        callClose();
      }
    });
  };

  useEffect(() => {
    const callApi = async () => {
      try {
        const { data = {} } = await api.read(`/policies/${id}`);
        setPolicy(data);
      } catch (error) {
        console.error(error);
      }
    };
    callApi();

    const abortApiCall = () => abortSignal && abortSignal.abort;
    return abortApiCall;
  }, [id, auth, reload, searchParamString]);

  const handleClassifierChange = async (selectedClassifier = null) => {
    try {
      setPolicies([]);
      if (selectedClassifier) {
        setClassifier(selectedClassifier);
        const { data = [] } = await api.list(
          `/client-policies/policy/${id}?classifier=${selectedClassifier}`
        );
        setPolicies(data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDispatchNotification = async () => {
    if (!classifier) {
      showAlert("Please select classifier", "error");
      classifierRef.current.focus();
    } else {
      if (markedPolicies.length > 0) {
        const data = {
          classifier: classifier,
          policies: markedPolicies,
        };

        const response = await api.add(
          `/client-policies/notify/${policy?.ClientId}`,
          data
        );

        if (response.ok) {
          showAlert("Notification sent!", "success");
          setMarkedPolicies([]);
          setPolicies([]);
          handleCloseSendNotice();
        }
      } else {
        showAlert("No policies selected", "error");
      }
    }
  };

  const headers = [
    {
      label: (
        <Form.Check
          type="switch"
          id="custom-switch-all"
          label=""
          checked={markedAll ? true : false}
          onChange={(e) => {
            if (e.target.checked) {
              const marked = [];
              policies.forEach((policy) => {
                marked.push(policy.id);
              });

              setMarkedPolicies(marked);
              setMarkedAll(true);
            } else {
              setMarkedPolicies([]);
              setMarkedAll(false);
            }
          }}
        />
      ),
    },
    { label: "Insuring Company" },
    { label: "Info" },
    { label: "Expires" },
    { label: "Status" },
  ];

  useEffect(() => {
    if (policies?.length > 0 && markedPolicies?.length == policies?.length) {
      setMarkedAll(true);
    } else {
      setMarkedAll(false);
    }
  }, [markedPolicies]);

  return (
    <section id="content" className="content">
      <div className="content__header content__boxed">
        <div className="content__wrap">
          {/* <!-- Breadcrumb --> */}
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/desktop">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/policies">Policies</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {policy?.Client?.name || id}
              </li>
            </ol>
          </nav>
          {/* <!-- END : Breadcrumb --> */}

          <h1 className="page-title mb-0 mt-2">
            {policy?.Client?.name || "Policy detail"}
          </h1>

          <p className="lead">Detail view of Policy</p>
        </div>
      </div>

      <div className="content__boxed">
        <div className="content__wrap">
          <div className="row">
            <div className="col-xxl-9 col-xl-8 col-lg-7 col-sm-12">
              {scope === "item" ? (
                <PolicyItemView />
              ) : (
                <PolicyItemList items={policy} allowAdd={true} scope={scope} />
              )}
            </div>
            <div className="col-xxl-3 col-xl-4 col-lg-5 col-sm-12">
              <div className="card mb-3">
                <div className="card-body">
                  <div
                    className="btn-group w-100 mb-4"
                    role="group"
                    aria-label="Basic example"
                  >
                    <Link
                      to={`/policies/${id}/edit`}
                      className="btn btn-secondary"
                    >
                      <i className="bi bi-pencil-square fs-4"></i>
                    </Link>
                    <Button
                      variant="info"
                      type="button"
                      title="Send Notification"
                      onClick={handleShowSendNotice}
                    >
                      <i className="bi bi-bell fs-4"></i>
                    </Button>
                    <Modal
                      show={showSendNotice}
                      onHide={handleCloseSendNotice}
                      backdrop="static"
                      keyboard={false}
                      size="lg"
                    >
                      <Modal.Header closeButton>
                        <Modal.Title>Policy Notifications</Modal.Title>
                      </Modal.Header>
                      <Modal.Body>
                        <Form>
                          <Row>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className="text-primary">
                                  Classification
                                </Form.Label>
                                <Form.Select
                                  ref={classifierRef}
                                  aria-label="Policy classifier"
                                  onChange={(e) =>
                                    handleClassifierChange(e.target.value)
                                  }
                                >
                                  <option value="">Select a classifier</option>
                                  <option value="general">
                                    General notice
                                  </option>
                                  <option value="new">
                                    Newly created notice
                                  </option>
                                  <option value="expiring">
                                    Expiring notice
                                  </option>
                                  <option value="renewal">
                                    Renewal notice
                                  </option>
                                  <option value="expired">
                                    Expired notice
                                  </option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                          </Row>
                          <div className="table-responsive bg-white mb-3">
                            <table className="table table-striped">
                              <TableHeader items={headers} />
                              <tbody>
                                {policies?.length > 0 &&
                                  policies?.map((p) => (
                                    <tr key={p?.id}>
                                      <td>
                                        <Form.Check
                                          type="switch"
                                          id={p?.id}
                                          label=""
                                          checked={
                                            markedPolicies.includes(p?.id)
                                              ? true
                                              : false
                                          }
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setMarkedPolicies([
                                                ...markedPolicies,
                                                p?.id,
                                              ]);
                                            } else {
                                              const exclude =
                                                markedPolicies.filter(
                                                  (marked) => marked !== p?.id
                                                );
                                              setMarkedPolicies(exclude);
                                            }
                                          }}
                                        />
                                      </td>
                                      <td>
                                        <span>{p?.Company?.name}</span>
                                        <br />
                                        <small>{p?.Branch?.name}</small>
                                        <br />
                                        <span>
                                          {p?.PolicyType?.name} -
                                          {p?.Product?.name}
                                        </span>
                                      </td>

                                      <td>
                                        <span>{p?.title}</span>
                                        <br />
                                        {p?.policyNumber && (
                                          <span className="text-primary">
                                            {p?.policyNumber}
                                          </span>
                                        )}
                                        <br />
                                        {p?.premium && (
                                          <span className="fw-bold text-info">
                                            Premium:{p.policyCurrency}{" "}
                                            {Number(p.premium).toLocaleString(
                                              "en-US"
                                            )}
                                          </span>
                                        )}
                                      </td>
                                      <td>
                                        {activeStatus.includes(p?.status) && (
                                          <>
                                            <span>
                                              {moment(p?.expires).format(
                                                "DD/MM/YYYY"
                                              ) || ""}
                                            </span>
                                            <p className="text-muted">
                                              {p?.expires &&
                                                moment().to(moment(p?.expires))}
                                            </p>
                                          </>
                                        )}
                                      </td>
                                      <td className="text-center fs-5">
                                        <PolicyItemStatus status={p?.status} />
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                            {policies?.length < 1 && (
                              <span>No Record found</span>
                            )}
                          </div>
                        </Form>
                      </Modal.Body>
                      <Modal.Footer>
                        <Button
                          variant="secondary"
                          onClick={handleCloseSendNotice}
                        >
                          Close
                        </Button>
                        <Button
                          variant="primary"
                          onClick={() => handleDispatchNotification()}
                        >
                          Submit
                        </Button>
                      </Modal.Footer>
                    </Modal>
                    {policy?.itemsCount < 1 && (
                      <button
                        type="button"
                        onClick={deletePolicy}
                        className="btn btn-danger"
                      >
                        <i className="bi bi-trash fs-4"></i>
                      </button>
                    )}
                  </div>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <Link
                      to={`/clients/${policy?.ClientId}`}
                      className="d-inline-block h5 btn-link text-truncate mb-2"
                    >
                      {policy?.Client?.name}
                    </Link>
                  </div>
                  <div className="fs-4">
                    <small className="text-primary">Client Phone number</small>
                    <p className="text-break border-bottom">
                      {policy?.Client?.phonenumber}
                    </p>
                    <small className="text-primary">Client Email</small>
                    <p className="text-break border-bottom">
                      {policy?.Client?.email}
                    </p>
                    <div>
                      <small className="text-primary">Policy Title</small>
                      <p className="text-break border-bottom">
                        {policy?.title}
                      </p>
                    </div>
                    <div>
                      <small className="text-primary">Policy Number</small>
                      <p className="text-break border-bottom">
                        {policy?.policyNumber}
                      </p>
                    </div>
                  </div>
                  <div className="d-grid gap-2  mt-3"></div>
                </div>
                <Accordion defaultActiveKey="0" flush>
                  <Accordion.Item eventKey="0">
                    <Accordion.Header className="text-primary">
                      Policy Info
                    </Accordion.Header>
                    <Accordion.Body>
                      <pre className="word-wrap:break-word">{policy?.info}</pre>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PolicyView;
